import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DetailHeader,
  StatTile,
  TrendChart,
  TopMerchantsCard,
  BreakdownCard,
  TransactionsTable,
} from '@/components/DetailBuildingBlocks';
import { formatMoney } from '@/lib/format';
import { colorForCategory } from '@/lib/categorize';
import { resolveCategoryName, computeSummary } from '@/lib/detailHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

/**
 * Phase 58B — CategoryDetail rewired to the paginated backend API.
 *
 * Route: /transactions/category/:slug
 * - Initial fetch loads PAGE_SIZE rows + the full summary block.
 * - "Show 50 more" CTA appends the next page in-place; summary/breakdowns
 *   don't refetch (they cover the entire window).
 */
export default function CategoryDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { profile, session } = useAuth();
  const currency = profile?.currency || 'INR';

  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [period, setPeriod] = useState(90);
  const [hasMore, setHasMore] = useState(false);

  // We need the live accounts list (for the rows' "account · category"
  // sub-label in the TransactionsTable). One cheap query.
  useEffect(() => {
    (async () => {
      const { data: accs } = await supabase.from('accounts').select('id, name, type, currency');
      setAccounts(accs || []);
    })();
  }, []);

  useEffect(() => {
    if (!session?.access_token) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: '0' });
        if (period) params.set('period_days', String(period));
        const res = await axios.get(`${BACKEND_URL}/api/transactions/category/${encodeURIComponent(slug)}?${params.toString()}`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (cancelled) return;
        setData(res.data);
        setRows(res.data.rows || []);
        setHasMore(!!res.data.has_more);
      } catch (e) {
        if (!cancelled) {
          setData(null);
          setRows([]);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, period, session?.access_token]);

  const loadMore = async () => {
    if (!session?.access_token || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(rows.length) });
      if (period) params.set('period_days', String(period));
      const res = await axios.get(`${BACKEND_URL}/api/transactions/category/${encodeURIComponent(slug)}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setRows((prev) => [...prev, ...(res.data.rows || [])]);
      setHasMore(!!res.data.has_more);
    } catch (e) {
      // soft-fail; user can re-click
    } finally {
      setLoadingMore(false);
    }
  };

  // Resolve category name client-side from the fetched data as a sturdy
  // fallback (the backend resolves it too, but if the request fails we
  // still want a humane title).
  const categoryName = data?.category || resolveCategoryName(slug, rows);
  const accent = colorForCategory(categoryName);
  const periodLabel = period === null ? 'All time' : `Last ${period} days`;

  const summary = data?.summary || {
    total_expense: 0, total_income: 0, count: 0, expense_count: 0, avg_per_month: 0, window_months: 1,
  };
  const merchants = data?.top_merchants || [];
  const byAccount = (data?.breakdown_by_account || []).map((b) => ({
    ...b,
    pct: b.pct,
  }));
  // Trend reuses the existing client helper across the loaded rows.
  // Since rows for a single category fit comfortably in memory, this is
  // fine; the heavy lifting (summary, breakdowns) already happens server-side.
  const trend = computeSummary(rows, period).trend;
  const useMonthly = !period || period > 120;

  return (
    <div className="space-y-5">
      <DetailHeader
        icon={Tag}
        title={categoryName}
        subtitle={loading
          ? 'Loading category…'
          : `${summary.expense_count} ${summary.expense_count === 1 ? 'transaction' : 'transactions'} · ${periodLabel}`}
        period={period}
        onPeriodChange={setPeriod}
        accent={accent}
      />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <StatTile
          label="Total spent"
          value={formatMoney(summary.total_expense, currency, { compact: true })}
          sub={periodLabel}
          testid="cat-stat-total"
        />
        <StatTile
          label="Avg per month"
          value={formatMoney(summary.avg_per_month, currency, { compact: true })}
          sub={`Over ${Number(summary.window_months || 1).toFixed(1)} months`}
          testid="cat-stat-avg"
        />
        <StatTile
          label="Transactions"
          value={String(summary.expense_count)}
          sub={summary.expense_count > 0 ? `${(summary.total_expense / Math.max(1, summary.expense_count)).toLocaleString(undefined, { maximumFractionDigits: 0 })} avg / txn` : '—'}
          testid="cat-stat-count"
        />
      </div>

      <div className="rounded-2xl border border-border/70 bg-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium">{useMonthly ? 'Monthly spend trend' : 'Weekly spend trend'}</div>
          <div className="text-xs text-muted-foreground">{periodLabel}</div>
        </div>
        <TrendChart data={trend} currency={currency} accent={accent} testid="cat-trend-chart" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <TopMerchantsCard rows={merchants} currency={currency} />
        <BreakdownCard
          title="By account"
          rows={byAccount}
          currency={currency}
          onRowClick={(name) => {
            const acct = accounts.find((a) => a.name === name);
            if (acct) navigate(`/accounts/${acct.id}/detail`);
          }}
          testid="cat-by-account"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">Transactions</div>
          {data?.total_rows ? (
            <div className="text-xs text-muted-foreground">
              Showing {Math.min(rows.length, data.total_rows)} of {data.total_rows}
            </div>
          ) : null}
        </div>
        <TransactionsTable
          rows={rows}
          currency={currency}
          accounts={accounts}
          emptyHint={loading ? 'Loading…' : 'No transactions matched this category in the selected period.'}
        />
        {hasMore && (
          <div className="flex justify-center pt-1">
            <Button
              variant="outline"
              onClick={loadMore}
              disabled={loadingMore}
              className="rounded-xl"
              data-testid="cat-show-more"
            >
              {loadingMore ? 'Loading…' : 'Show 50 more'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
